import { Capacitor } from "@capacitor/core";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch
} from "react-router-dom";
import "./App.css";
import NotFound from "./components/404";
import AppUrlListener from "./components/AppUrlListener";
import MobileRedirect from "./components/pages/MobileRedirect";

import Navbar from "./components/elements/navbar";
import ToastContainer from "./components/elements/ToastContainer";
import CrispWeb from "./components/menus/crisp";
import Footer from "./components/menus/footer";
import UnderMaintenance from "./components/under-maintenance-modern";

// Core customer flow layouts - keep eager loaded
import LayoutCart from "./components/layouts/layout-cart";
import LayoutChefFood from "./components/layouts/layout-chef-food";
import Layout from "./components/layouts/layout-home";
import LayoutHomeChef from "./components/layouts/layout-home-chef";

import { FEATURE_FLAGS } from "./components/constants/featureFlags";
import { GET } from "./components/constants/request-type";
import { service_api } from "./components/constants/url";
import { MakeRequestAsync } from "./components/functions/axios";
import { decryptSingleData } from "./components/functions/cryptojs";
import { PlatformProvider } from "./components/platform";
import { useAuthRestore } from "./hooks/useAuthRestore";
import { setupForegroundMessageHandler, testLocalNotification } from "./utils/firebaseMessaging";
import { usePushTokenRegistration } from "./hooks/usePushTokenRegistration";
import { useLanguage } from "./hooks/useLanguage";
import { POST_SETTING } from "./store/action";
import { createLogger } from "./utils/logger";
import { shouldUseIonicComponents } from "./utils/platformUtils";
import { hasStoredAuth } from "./utils/authStorageUtils";
import { showToast } from "./utils/toastManager";

// Lazy load MobileApp so Ionic CSS only loads on native platforms
const MobileApp = React.lazy(() => import("./components/mobile/MobileApp"));

// Lazy load WelcomeScreen for first-time native app users
const WelcomeScreen = React.lazy(
  () => import("./components/mobile/WelcomeScreen")
);

// Lazy load chef dashboard (heavy antd usage, accessed by ~10% of users)
const LayoutIamChef = React.lazy(
  () => import("./components/layouts/layout-iam-chef")
);

// Lazy load infrequently accessed pages
const LayoutBecome = React.lazy(
  () => import("./components/layouts/layout-become")
);
const LayoutBlog = React.lazy(() => import("./components/layouts/layout-blog"));
const LayoutSuccess = React.lazy(
  () => import("./components/layouts/layout-order-success")
);
const LayoutSuccessGlocery = React.lazy(
  () => import("./components/layouts/layout-order-glocery-success")
);
const LayoutUserOrders = React.lazy(
  () => import("./components/layouts/layout-user-orders")
);
const LayoutUserProfile = React.lazy(
  () => import("./components/layouts/layout-user-profile")
);

// Lazy load informational pages
const LayoutAboutus = React.lazy(
  () => import("./components/layouts/layout-aboutus")
);
const LayoutTerms = React.lazy(
  () => import("./components/layouts/layout-terms")
);
const LayoutAcademy = React.lazy(
  () => import("./components/layouts/layout-academy")
);
const ResetPasswordPage = React.lazy(
  () => import("./components/pages/ResetPasswordPage")
);
const LayoutCareer = React.lazy(
  () => import("./components/layouts/layout-career")
);
const LayoutFoodSafety = React.lazy(
  () => import("./components/layouts/layout-food-safety")
);
const LayoutTermsService = React.lazy(
  () => import("./components/layouts/layout-terms-service")
);

const logger = createLogger("App");

function App() {
  const [search, setSearch] = useState("");
  const [useMobileUI] = useState(shouldUseIonicComponents());
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const prevUserIdRef = useRef(null);

  const isCapacitor = Capacitor.isNativePlatform();
  const userData = useSelector((state) => state.user);
  const setting = useSelector((state) => state.setting);

  // Show WelcomeScreen on Capacitor when logged out (reset when user logs out)
  const shouldShowWelcome = isCapacitor && !userData?._id && !hasStoredAuth();
  const showWelcomeScreen = shouldShowWelcome && !welcomeDismissed;

  // Initialize language system
  useLanguage();

  // Add CSS class to body only on native mobile platforms
  useEffect(() => {
    if (useMobileUI) {
      document.body.classList.add("capacitor-mobile");
      return () => {
        document.body.classList.remove("capacitor-mobile");
      };
    }
  }, []);

  // Restore authentication from localStorage
  useAuthRestore();

  // Register FCM push token for already-logged-in users (chefs receive pickup OTP etc.)
  usePushTokenRegistration();

  // Handle foreground push notifications
  useEffect(() => {
    if (Capacitor.getPlatform() === "web") {
      setupForegroundMessageHandler();
      window.__testPaniNotification = testLocalNotification;
      const onFcmMessage = (e) => {
        const { title, body } = e.detail || {};
        const msg = title ? `${title}: ${body}` : body || "New notification";
        showToast("info", msg);
      };
      window.addEventListener("pani-fcm-message", onFcmMessage);
      return () => window.removeEventListener("pani-fcm-message", onFcmMessage);
    }
    // Native (iOS/Android): listen to Capacitor Firebase plugin for in-app toast
    if (Capacitor.isNativePlatform()) {
      const listenerRef = { current: null };
      import("@capacitor-firebase/messaging").then(({ FirebaseMessaging }) => {
        return FirebaseMessaging.addListener(
          "notificationReceived",
          (event) => {
            const notif = event?.notification;
            const data = notif?.data || {};
            const title = notif?.title ?? data?.title ?? (notif?.body || data?.body ? "" : "Pani");
            const body = notif?.body ?? data?.body ?? notif?.title ?? data?.title ?? "";
            const msg = title ? `${title}: ${body}` : body || "New notification";
            showToast("info", msg);
          }
        );
      }).then((handle) => { listenerRef.current = handle; });
      return () => listenerRef.current?.remove?.();
    }
  }, []);

  const dispatch = useDispatch();
  const onSearch = (value) => setSearch(value);

  // Reset welcome screen when user logs out (so they see it again next time)
  useEffect(() => {
    const hadUser = prevUserIdRef.current != null;
    const hasUser = !!userData?._id;
    if (hadUser && !hasUser) {
      setWelcomeDismissed(false);
    }
    prevUserIdRef.current = userData?._id ?? null;
  }, [userData?._id]);

  // Handle welcome screen completion (language + address selected)
  const handleWelcomeLanguageSelected = (language) => {
    logger.log(`Welcome screen completed with language: ${language}`);
    setWelcomeDismissed(true);
  };

  useEffect(() => {
    const Setting = (value) => dispatch(post_setting(value));

    const initialData = async () => {
      const request_details_chef = {
        type: GET,
        url: service_api,
        route: `country/get-one-by-country/+1`,
        token: null
      };

      try {
        const response = await MakeRequestAsync(request_details_chef);
        const decryptedData = await decryptSingleData(response.data.country);
        Setting(decryptedData);
      } catch (err) {
        logger.log("We couldn't fetch data: ", err?.response?.data?.message);
      }
    };

    initialData();
  }, [dispatch]);

  // Render routes - same for both mobile and desktop
  const renderRoutes = () => (
    <Switch>
      <Route exact path="/">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        {useMobileUI ? <Redirect to="/explore/chef" /> : <Layout />}
      </Route>
      <Route exact path="/become-a-chef">
        {useMobileUI && <Redirect to="/" />}
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutBecome />
      </Route>
      <Route exact path="/explore/chef">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutHomeChef />
      </Route>
      <Route exact path="/user/profile">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutUserProfile />
      </Route>
      <Route exact path="/user/orders">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutUserOrders />
      </Route>
      <Route exact path="/order/chef/menu">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutChefFood />
      </Route>
      <Route exact path="/order/chef/menu/cart">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutCart />
      </Route>
      <Route exact path="/order/sucessful/complete">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutSuccess />
      </Route>
      <Route exact path="/chef/iam-a-chef/home">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/cuisine">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/address">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/profil">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/cooking-delivery-day">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/order">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/menu">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/edit-menu">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/add-menu">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/order">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/glocery">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/order-glocery/sucessful/complete">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutSuccessGlocery />
      </Route>
      <Route exact path="/chef/iam-a-chef/withdrawal">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/banking">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      {FEATURE_FLAGS.blogEnabled && (
        <Route exact path="/chef/iam-a-chef/blog">
          {setting?.general?.maintenance === true && (
            <Redirect to="/under-maintenance" />
          )}
          <LayoutIamChef />
        </Route>
      )}
      <Route exact path="/chef/iam-a-chef/hub">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      <Route exact path="/chef/iam-a-chef/help">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef />
      </Route>
      {FEATURE_FLAGS.blogEnabled && (
        <Route exact path="/blog">
          {setting?.general?.maintenance === true && (
            <Redirect to="/under-maintenance" />
          )}
          <LayoutBlog />
        </Route>
      )}
      <Route exact path="/support-crisp-webview">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <CrispWeb />
      </Route>
      <Route exact path="/terms-of-service">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutTermsService />
      </Route>
      <Route exact path="/privacy-policy">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutTerms />
      </Route>
      <Route exact path="/community-guidelines">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutTerms />
      </Route>
      <Route exact path="/food-safety">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutFoodSafety />
      </Route>
      <Route exact path="/our-story">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutAboutus />
      </Route>
      <Route exact path="/careers">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutCareer />
      </Route>
      <Route exact path="/panichefs-academy">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutAcademy />
      </Route>
      <Route exact path="/chef/iam-a-chef/certificate">
        {setting?.general?.maintenance === true && (
          <Redirect to="/under-maintenance" />
        )}
        <LayoutIamChef setting={setting} />
      </Route>
      <Route exact path="/under-maintenance">
        {setting?.general?.maintenance === false && <Redirect to="/" />}
        <UnderMaintenance setting={setting} />
      </Route>
      <Route exact path="/mobile-redirect">
        <MobileRedirect />
      </Route>
      <Route exact path="/reset-password">
        <ResetPasswordPage />
      </Route>
      <Route exact path="*">
        <NotFound />
      </Route>
    </Switch>
  );

  // Conditionally render with Mobile or Desktop wrapper
  if (useMobileUI) {
    // Mobile: Router wraps both WelcomeScreen and MobileApp so hooks like useLocation work in both
    return (
      <PlatformProvider>
        <div className="App">
          <Router>
            {Capacitor.isNativePlatform() && <AppUrlListener />}
            <Suspense
              fallback={<div style={{ height: "100vh", background: "#fff" }} />}
            >
              <MobileApp>
                {isCapacitor && showWelcomeScreen ? (
                  <WelcomeScreen
                    onLanguageSelected={handleWelcomeLanguageSelected}
                  />
                ) : (
                  <>
                    <Navbar
                      onSearch={(text) => onSearch(text)}
                      textValue={search}
                      landing={true}
                    />
                    {renderRoutes()}
                    {/* Footer hidden on mobile */}
                    <ToastContainer />
                  </>
                )}
              </MobileApp>
            </Suspense>
          </Router>
        </div>
      </PlatformProvider>
    );
  }

  // Desktop: Use standard web layout
  return (
    <PlatformProvider>
      <div className="App">
        <Router>
          {useMobileUI && <AppUrlListener />}
          <div style={{ height: "80px" }}>
            <Navbar
              onSearch={(text) => onSearch(text)}
              textValue={search}
              landing={true}
            />
          </div>
          <Suspense
            fallback={<div style={{ height: "100vh", background: "#fff" }} />}
          >
            {renderRoutes()}
          </Suspense>
          {/* Show footer only on web */}
          {Capacitor.getPlatform() === "web" && <Footer />}
        </Router>
        <ToastContainer />
      </div>
    </PlatformProvider>
  );
}

export default App;

const post_setting = (data) => ({
  type: POST_SETTING,
  data: data
});
